import React, { useRef, useState, useCallback, type DragEvent, type ClipboardEvent } from 'react';
import { ArrowUp, CaretRight, Files, Plus, Stop } from '@phosphor-icons/react';

import { RichTextInput, type RichTextInputHandle } from '../rich-text-input';
import { cn } from '../../lib/utils';
import { useConversation } from './ConversationContext';
import type { Attachment, Collection, ContextPill, FileAttachment, Page } from './types';
import { ContextPillBadge } from './ContextPillBadge';
import { InputImageThumbnails } from './AttachmentPreviews';
import { mimeTypeToIconType } from './attachmentUtils';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';

export interface ConversationInputProps {
  placeholder?: string;
  attachments?: Attachment[];
  collections?: Collection[];
  pages?: Page[];
  acceptedFileTypes?: string[];
  disabled?: boolean;
  isElevated?: boolean;
  className?: string;
}

const COLLECTION_SEARCH_THRESHOLD = 5;
const MAX_FILE_ATTACHMENTS = 10;

function isFileAccepted(file: File, acceptedTypes?: string[]): boolean {
  if (!acceptedTypes || acceptedTypes.length === 0) return true;

  return acceptedTypes.some((type) => {
    if (type.startsWith('.')) return file.name.toLowerCase().endsWith(type.toLowerCase());
    if (type.endsWith('/*')) return file.type.startsWith(type.slice(0, -1));

    return file.type === type;
  });
}

function attachmentToPill(attachment: Attachment): ContextPill {
  switch (attachment.type) {
    case 'file':
      return {
        id: attachment.id,
        label: attachment.file.name,
        iconType: mimeTypeToIconType(attachment.file.mimeType),
        loading: attachment.loading,
        progress: attachment.progress,
      };
    case 'page':
      return { id: attachment.id, label: attachment.page.title, iconType: 'page' };
    case 'entity':
      return { id: attachment.id, label: attachment.entity.title, iconType: attachment.entity.type };
  }
}

export const ConversationInput = ({
  placeholder = 'Ask anything…',
  attachments = [],
  collections = [],
  pages = [],
  acceptedFileTypes,
  disabled = false,
  isElevated = false,
  className,
}: ConversationInputProps) => {
  const { callbacks, isStreaming } = useConversation();
  const [hasText, setHasText] = useState(false);
  const latestMarkdown = useRef('');
  const editorRef = useRef<RichTextInputHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const [menuOpen, setMenuOpen] = useState(false);
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);
  const collectionCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collectionHasFocus = useRef(false);

  const openCollectionPicker = useCallback(() => {
    if (collectionCloseTimer.current) {
      clearTimeout(collectionCloseTimer.current);
      collectionCloseTimer.current = null;
    }
    setCollectionPickerOpen(true);
  }, []);

  const scheduleCollectionPickerClose = useCallback(() => {
    collectionCloseTimer.current = setTimeout(() => {
      if (!collectionHasFocus.current) {
        setCollectionPickerOpen(false);
      }
    }, 150);
  }, []);

  const imageAttachments = attachments.filter(
    (a): a is FileAttachment => a.type === 'file' && a.file.mimeType.startsWith('image/'),
  );
  const otherAttachments = attachments.filter((a) => !(a.type === 'file' && a.file.mimeType.startsWith('image/')));
  const pills = otherAttachments.map(attachmentToPill);
  const hasUploadingAttachments = attachments.some((a) => a.type === 'file' && a.loading);
  const isSendDisabled = disabled || isStreaming || hasUploadingAttachments;
  const showMenu = collections.length > 0 || pages.length > 0;

  const handleSend = useCallback(
    (markdown?: string) => {
      const trimmed = (markdown ?? latestMarkdown.current).trim();
      if (!trimmed || isSendDisabled) return;
      callbacks.onSend?.(trimmed, attachments.length > 0 ? attachments : undefined);
      editorRef.current?.clear();
      editorRef.current?.focus();
    },
    [isSendDisabled, callbacks, attachments],
  );

  const handleStop = useCallback(() => {
    callbacks.onStop?.();
  }, [callbacks]);

  const handleCollectionSelect = useCallback(
    (collection: Collection) => {
      setCollectionPickerOpen(false);
      setMenuOpen(false);
      callbacks.onAttachmentAdd?.({
        type: 'select-entity',
        entity: { id: collection.id, type: 'collection', title: collection.title, url: collection.url },
      });
    },
    [callbacks],
  );

  const handlePageSelect = useCallback(() => {
    if (!pages[0]) return;
    setMenuOpen(false);
    callbacks.onAttachmentAdd?.({ type: 'select-page', page: pages[0] });
  }, [pages, callbacks]);

  const fileAttachmentCount = attachments.filter((a) => a.type === 'file').length;

  const addFiles = useCallback(
    (files: File[]) => {
      const toAdd = files.slice(0, MAX_FILE_ATTACHMENTS - fileAttachmentCount);
      if (toAdd.length < files.length) alert(`You can only attach up to ${MAX_FILE_ATTACHMENTS} files at a time.`);
      if (toAdd.length > 0) callbacks.onAttachmentAdd?.({ type: 'add-files', files: toAdd });
    },
    [fileAttachmentCount, callbacks],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      if (files.length > 0) addFiles(files);
    },
    [addFiles],
  );

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      if (disabled || isStreaming) return;
      const dropped = Array.from(e.dataTransfer.files);
      const files = dropped.filter((f) => isFileAccepted(f, acceptedFileTypes));
      if (dropped.length > 0 && files.length === 0) {
        alert("That file type isn't supported. Please try a different format.");

        return;
      }
      if (files.length > 0) addFiles(files);
    },
    [disabled, isStreaming, acceptedFileTypes, addFiles],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      if (disabled || isStreaming) return;
      const files: File[] = [];
      if (e.clipboardData.files.length > 0) {
        files.push(...Array.from(e.clipboardData.files));
      } else {
        for (const item of Array.from(e.clipboardData.items)) {
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) files.push(file);
          }
        }
      }
      const accepted = files.filter((f) => isFileAccepted(f, acceptedFileTypes));
      if (files.length > 0 && accepted.length === 0) {
        e.preventDefault();
        e.stopPropagation();
        alert("That file type isn't supported. Please try a different format.");

        return;
      }
      if (accepted.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        addFiles(accepted);
      }
    },
    [disabled, isStreaming, acceptedFileTypes, addFiles],
  );

  return (
    <div className={cn('w-full', className)}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFileTypes?.join(',')}
        className="sr-only"
        onChange={handleFileChange}
      />
      <div
        className={cn(
          'relative w-full rounded-t-2xl rounded-b-lg bg-gradient-yellowfin p-2 transition-shadow duration-300',
          isElevated && 'shadow-[0_-20px_25px_-5px_rgba(0,0,0,0.1),0_-8px_10px_-6px_rgba(0,0,0,0.1)]',
        )}
      >
        <div
          className="relative w-full rounded-t-lg rounded-b bg-surface shadow-md"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onPasteCapture={handlePaste}
        >
          {isDragging && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-t-lg rounded-b pointer-events-none"
              style={{ backgroundColor: 'color-mix(in srgb, var(--bfc-surface) 90%, transparent)' }}
            >
              <div className="flex flex-col items-center gap-1.5">
                <Files className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Drop files here</span>
              </div>
            </div>
          )}
          <InputImageThumbnails
            attachments={imageAttachments}
            onRemove={(a) => callbacks.onAttachmentRemove?.(a)}
            className="px-3 pt-3"
          />
          {pills.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 pt-3">
              {pills.map((pill) => {
                const attachment = otherAttachments.find((a) => a.id === pill.id);

                return (
                  <ContextPillBadge
                    key={pill.id}
                    pill={pill}
                    onRemove={attachment ? () => callbacks.onAttachmentRemove?.(attachment) : undefined}
                  />
                );
              })}
            </div>
          )}

          <RichTextInput
            ref={editorRef}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
            className="w-full bg-transparent p-4 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            onChange={(markdown) => {
              latestMarkdown.current = markdown;
              setHasText(markdown.trim().length > 0);
            }}
            onSubmit={handleSend}
          />

          <div className="flex items-center justify-between px-2 pb-2">
            <div className="flex items-center gap-1">
              {showMenu ? (
                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                  <DropdownMenuTrigger asChild disabled={disabled || isStreaming}>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex h-6 w-6 items-center justify-center rounded text-default hover:bg-muted transition-all',
                        menuOpen && 'shadow-md dark:shadow-md',
                      )}
                      disabled={disabled || isStreaming}
                    >
                      <Plus className={cn('h-4 w-4 transition-transform duration-200', menuOpen && '-rotate-45')} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    side="right"
                    className="rounded border-none shadow-md bg-surface p-2 min-w-[200px] dark:border-none dark:shadow-md"
                  >
                    {collections.length > 0 && (
                      <Popover open={collectionPickerOpen} onOpenChange={setCollectionPickerOpen}>
                        <PopoverTrigger asChild>
                          <DropdownMenuItem
                            className="p-2"
                            onSelect={(e) => e.preventDefault()}
                            onMouseEnter={openCollectionPicker}
                            onMouseLeave={scheduleCollectionPickerClose}
                            onFocus={openCollectionPicker}
                          >
                            Collection
                            <CaretRight className="ml-auto h-4 w-4" />
                          </DropdownMenuItem>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[200px] rounded border-none p-0 shadow-md dark:border-none dark:shadow-md"
                          side="right"
                          align="start"
                          onMouseEnter={openCollectionPicker}
                          onMouseLeave={scheduleCollectionPickerClose}
                          onFocusCapture={() => {
                            collectionHasFocus.current = true;
                          }}
                          onBlurCapture={() => {
                            collectionHasFocus.current = false;
                            scheduleCollectionPickerClose();
                          }}
                        >
                          <Command>
                            {collections.length > COLLECTION_SEARCH_THRESHOLD && (
                              <CommandInput placeholder="Search collections..." />
                            )}
                            <CommandList>
                              <CommandEmpty>No collections found.</CommandEmpty>
                              <CommandGroup>
                                {collections.map((collection) => (
                                  <CommandItem
                                    key={collection.id}
                                    value={collection.title}
                                    onSelect={() => handleCollectionSelect(collection)}
                                  >
                                    {collection.title}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                    {pages.length > 0 && (
                      <DropdownMenuItem className="p-2" onSelect={handlePageSelect}>
                        Attach page
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem className="p-2" onSelect={() => fileInputRef.current?.click()}>
                      Add image or file
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  type="button"
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-default hover:bg-muted transition-all"
                  disabled={disabled || isStreaming}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
            {isStreaming ? (
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded transition-colors bg-input text-primary-foreground hover:bg-input/90"
                onClick={handleStop}
                disabled={disabled}
              >
                <Stop className="h-4 w-4" weight="fill" />
              </button>
            ) : (
              <button
                type="button"
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded transition-colors',
                  hasText && !hasUploadingAttachments
                    ? 'bg-input text-primary-foreground hover:bg-input/90'
                    : 'bg-muted text-muted-foreground',
                )}
                onClick={() => handleSend()}
                disabled={isSendDisabled || !hasText}
              >
                <ArrowUp className="h-4 w-4" weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
